import { IPanToBounds } from 'models';
import {
	ICommentsAverage,
	IPointWithOffset,
} from 'modules/admin-panel/pages/results/models';

// represents zoom types of selected map point
export enum POINT_ZOOM_TYPES {
	country = 'country',
	city = 'city',
	office = 'office',
}

const DEGREES_IN_CIRCLE = 360;
const OFFICE_OFFSET = 0.002;

const getCoordinatesForMapBound = (
	zoomType: POINT_ZOOM_TYPES,
	mapPoint: ICommentsAverage,
) =>
	({
		sw: {
			lat: mapPoint[`${zoomType}SouthwestLatitude` as keyof ICommentsAverage],
			lng: mapPoint[`${zoomType}SouthwestLongitude` as keyof ICommentsAverage],
		},
		ne: {
			lat: mapPoint[`${zoomType}NortheastLatitude` as keyof ICommentsAverage],
			lng: mapPoint[`${zoomType}NortheastLongitude` as keyof ICommentsAverage],
		},
	} as IPanToBounds);

export const getCityAndOfficeCoordinates = (mapPoint: ICommentsAverage) => ({
	cityCoordinates: getCoordinatesForMapBound(POINT_ZOOM_TYPES.city, mapPoint),
	officeCoordinates: getCoordinatesForMapBound(
		POINT_ZOOM_TYPES.office,
		mapPoint,
	),
});

export const officeCoordinatesEqualForAllPoints = (
	points: ICommentsAverage[],
) => {
	if (!points[0].office) return false;
	const {
		officeSouthwestLongitude,
		officeSouthwestLatitude,
		officeNortheastLongitude,
		officeNortheastLatitude,
	} = points[0];
	const pointsWithEqualOfficeCoordinates = points.filter(
		(point) =>
			point.officeSouthwestLongitude === officeSouthwestLongitude &&
			point.officeSouthwestLatitude === officeSouthwestLatitude &&
			point.officeNortheastLongitude === officeNortheastLongitude &&
			point.officeNortheastLatitude === officeNortheastLatitude,
	);
	return pointsWithEqualOfficeCoordinates.length === points.length;
};

export const officeAndCityCoordinatesEqual = (mapPoint: ICommentsAverage) => {
	const {
		citySouthwestLongitude,
		citySouthwestLatitude,
		cityNortheastLongitude,
		cityNortheastLatitude,
		officeSouthwestLongitude,
		officeSouthwestLatitude,
		officeNortheastLongitude,
		officeNortheastLatitude,
	} = mapPoint;
	return (
		officeSouthwestLongitude === citySouthwestLongitude &&
		officeSouthwestLatitude === citySouthwestLatitude &&
		officeNortheastLongitude === cityNortheastLongitude &&
		officeNortheastLatitude === cityNortheastLatitude
	);
};

export const areOfficePointsTheSame = (
	firstPoint: ICommentsAverage,
	secondPoint: ICommentsAverage,
) =>
	firstPoint.office === secondPoint.office &&
	firstPoint.latitude === secondPoint.latitude &&
	firstPoint.longitude === secondPoint.longitude;

export const makeMarkersWithOffset = (
	markerList?: ICommentsAverage[] | null,
): IPointWithOffset[] | undefined => {
	if (!markerList) return;

	const checkedLocations: string[] = [];
	return markerList.reduce((resultList, marker) => {
		const { country, city } = marker;
		if (!country || !city || !officeAndCityCoordinatesEqual(marker)) {
			return resultList.concat(marker);
		}
		if (checkedLocations.includes(`${country} ${city}`)) {
			return resultList;
		}

		const pointsFromOneCityWithoutAddress = markerList.reduce((list, point) => {
			if (
				point.country === country &&
				point.city === city &&
				officeAndCityCoordinatesEqual(point)
			)
				list.push(point);
			return list;
		}, [] as ICommentsAverage[]);

		if (pointsFromOneCityWithoutAddress.length === 1) {
			return resultList.concat(marker);
		}

		const newPoints = pointsFromOneCityWithoutAddress.map((point, i) => {
			const quot = DEGREES_IN_CIRCLE / pointsFromOneCityWithoutAddress.length;
			return {
				...point,
				latitude: point.latitude + OFFICE_OFFSET * Math.cos(quot * i),
				longitude: point.longitude + OFFICE_OFFSET * Math.sin(quot * i),
				hasOfficeOffset: true,
			};
		});
		checkedLocations.push(`${country} ${city}`);
		return resultList.concat(newPoints);
	}, [] as IPointWithOffset[]);
};
